/**
 * X12 278 (005010X217) segment metadata — mirrors implementation guide language.
 */
export const SEGMENT_GUIDE = {
  ISA: {
    description:
      "Interchange Control Header. Identifies sender, receiver, date, and control settings for the entire interchange.",
    purpose: "Opens the X12 interchange envelope (ISA/IEA pair).",
  },
  GS: {
    description:
      "Functional Group Header. Defines the functional group (HI = Health Care Services Review) and version.",
    purpose: "Groups one or more 278 transaction sets under a single functional group.",
  },
  ST: {
    description:
      "Transaction Set Header. Identifies the transaction as 278 and references the implementation guide.",
    purpose: "Begins the 278 Health Care Services Review transaction set.",
  },
  BHT: {
    description:
      "Beginning of Hierarchical Transaction. Carries purpose, reference ID, and timestamp for the request.",
    purpose: "Establishes request vs. response, cancellation, or re-submission context.",
  },
  HL_2000A: {
    description:
      "Hierarchical Level segment for loop 2000A. Level code 20 = Information Source (payer/UMO).",
    purpose: "Anchors the payer hierarchy under the transaction header.",
  },
  NM1_PR: {
    description:
      "Individual or Organizational Name — payer (entity ID PR). Identifies the utilization management organization.",
    purpose: "Names the payer receiving the prior authorization request.",
  },
  PER_IC: {
    description:
      "Administrative Communications Contact for the payer (contact function IC).",
    purpose: "Provides payer EDI support phone or email when required by trading partner.",
  },
  HL_2000B: {
    description:
      "Hierarchical Level for loop 2000B. Level code 21 = Information Receiver (requesting provider).",
    purpose: "Anchors the requester under the payer level.",
  },
  NM1_1P: {
    description:
      "Name segment for the requesting provider (entity ID 1P). Person or organization.",
    purpose: "Identifies who is submitting the authorization request.",
  },
  PER_1P: {
    description:
      "Contact information for the requesting provider (contact function 1P).",
    purpose: "Supplies a reachable contact for clinical or administrative follow-up.",
  },
  HL_2000C: {
    description:
      "Hierarchical Level for loop 2000C. Level code 22 = Subscriber.",
    purpose: "Anchors the insured member under the requester.",
  },
  NM1_IL: {
    description:
      "Name segment for the insured subscriber (entity ID IL).",
    purpose: "Identifies the policyholder associated with the coverage.",
  },
  DMG_IL: {
    description:
      "Demographic information for the subscriber when patient is the subscriber.",
    purpose: "Conveys birth date and gender for eligibility and clinical context.",
  },
  HL_2000E: {
    description:
      "Hierarchical Level for loop 2000E. Level code EV = Patient Event.",
    purpose: "Anchors the clinical event (authorization request) under the patient level.",
  },
  TRN_EVENT: {
    description:
      "Patient Event Tracking Number. Uniquely identifies the patient event for tracking purposes.",
    purpose: "Supports payer correlation and traceability (POS 0200, Usage S).",
  },
  UM_EVENT: {
    description:
      "Health Care Services Review Information. Request category, certification action, service type, and place of service.",
    purpose: "Core UM request segment (POS 0400, Usage R).",
  },
  REF_BB: {
    description: "Previous Review Authorization Number (qualifier BB).",
    purpose: "References a prior authorization for extension, appeal, or cancellation.",
  },
  REF_ADMIN: {
    description: "Previous Review Administrative Reference Number (qualifier 9F).",
    purpose: "Administrative reference tied to a prior review.",
  },
  DTP_ACCIDENT: {
    description: "Accident Date (qualifier 439).",
    purpose: "Date of accident when injury-related services are requested.",
  },
  DTP_LMP: {
    description: "Last Menstrual Period Date (qualifier 484).",
    purpose: "Used for maternity-related authorization requests.",
  },
  DTP_EDB: {
    description: "Estimated Date of Birth (qualifier 455).",
    purpose: "Expected delivery date for maternity services.",
  },
  DTP_ONSET: {
    description: "Onset of Current Symptoms or Illness Date (qualifier 431).",
    purpose: "Clinical onset date supporting medical necessity.",
  },
  DTP_EVENT: {
    description: "Event Date / institutional service period (qualifier 435).",
    purpose: "Admission or event date range for institutional requests.",
  },
  DTP_ADMISSION: {
    description: "Admission Date (qualifier 435, format D8).",
    purpose: "Single-date admission for inpatient authorization.",
  },
  DTP_DISCHARGE: {
    description: "Discharge Date (qualifier 096).",
    purpose: "Expected or actual discharge date for inpatient stays.",
  },
  HI_EVENT: {
    description: "Patient Diagnosis. ICD-10 diagnosis codes (HI01–HI12).",
    purpose: "Conveys primary and secondary diagnoses for the event (POS 0800, Usage S).",
  },
  HSD_LOS: {
    description: "Health Care Services Delivery — length of stay in days.",
    purpose: "Requested inpatient days (e.g. HSD*DY*7).",
  },
  HSD_FREQ: {
    description: "Health Care Services Delivery — visit frequency.",
    purpose: "Recurring service frequency (e.g. home health visits per week).",
  },
  CRC_AMBULANCE: {
    description: "Ambulance Certification Information (code category 07).",
    purpose: "Certifies ambulance transport medical necessity.",
  },
  CRC_CHIRO: {
    description: "Chiropractic Certification Information (code category 08).",
    purpose: "Chiropractic treatment certification indicators.",
  },
  CRC_DME: {
    description: "Durable Medical Equipment Certification (code category 09).",
    purpose: "DME medical necessity and condition codes.",
  },
  CRC_OXYGEN: {
    description: "Oxygen Therapy Certification Information (code category E1).",
    purpose: "Home oxygen therapy certification.",
  },
  CL1_EVENT: {
    description: "Institutional Claim Code — admission type.",
    purpose: "Admission type (emergency, urgent, elective) for institutional AR requests (POS 1100).",
  },
  CR1_EVENT: {
    description: "Ambulance Transport Information including mileage and reason.",
    purpose: "Ambulance transport details (POS 1200, Usage S).",
  },
  CR2_EVENT: {
    description: "Spinal Manipulation Service Information.",
    purpose: "Chiropractic spinal manipulation treatment details.",
  },
  CR5_EVENT: {
    description: "Home Oxygen Therapy Information.",
    purpose: "Oxygen flow rate, equipment type, and certification period.",
  },
  CR6_EVENT: {
    description: "Home Health Care Information including certification period.",
    purpose: "Home health start/end dates and visit pattern.",
  },
  PWK_EVENT: {
    description: "Additional Patient Information — attachment/paperwork data.",
    purpose: "Identifies supporting clinical documentation (POS 1550, repeat up to 10).",
  },
  NM1_FA: {
    description: "Patient Event Provider Name — facility (entity FA, loop 2010EA).",
    purpose: "Identifies the facility where services will be rendered.",
  },
  N3_FA: {
    description: "Facility street address for the patient event provider.",
    purpose: "Address line(s) for the rendering facility.",
  },
  N4_FA: {
    description: "Facility city, state, and postal code.",
    purpose: "Geographic location of the rendering facility.",
  },
  NM1_EP: {
    description: "Patient Event Provider Name — attending or rendering provider.",
    purpose: "Additional event-level provider (loop 2010EA, repeat up to 14).",
  },
  REF_EP: {
    description: "Patient Event Provider Supplemental Information.",
    purpose: "Supplemental IDs for event-level providers (repeat up to 7).",
  },
  DTP_472: {
    description:
      "Date or Time Period with qualifier 472 (Service).",
    purpose: "States the date or period of service for the event.",
  },
  MSG_EVENT: {
    description: "Free-form message text related to the patient event.",
    purpose: "Carries narrative clinical or administrative notes.",
  },
  HL_2000F: {
    description:
      "Hierarchical Level for loop 2000F. Level code SS = Service Line.",
    purpose: "Repeats per requested procedure or service line.",
  },
  SV1: {
    description:
      "Professional Service line. Procedure code, charge, units, and diagnosis pointer.",
    purpose: "Details each billable service requiring authorization.",
  },
  SE: {
    description:
      "Transaction Set Trailer. Segment count and control number.",
    purpose: "Closes the 278 transaction set (ST/SE pair).",
  },
  GE: {
    description:
      "Functional Group Trailer. Count of transaction sets in the group.",
    purpose: "Closes the functional group (GS/GE pair).",
  },
  IEA: {
    description:
      "Interchange Control Trailer. Count of functional groups in the interchange.",
    purpose: "Closes the interchange envelope (ISA/IEA pair).",
  },
};

export function getSegmentGuide(segmentId) {
  return (
    SEGMENT_GUIDE[segmentId] ?? {
      description: "Segment defined in 005010X217 Health Care Services Review Request.",
      purpose: "Supports prior authorization data exchange per X12 TR3.",
    }
  );
}
